import React from 'react';

const INK = '#172554';
const BLUE = '#2563eb';
const denominatorValid = (value) => Number.isInteger(value) && value >= 1 && value <= 100;
const fractionValid = (numerator, denominator) => denominatorValid(denominator) && Number.isInteger(numerator) && numerator >= 0 && numerator <= denominator;
const dataValid = (data) => data && typeof data === 'object' && !Array.isArray(data) && Object.keys(data).length >= 1 && Object.keys(data).length <= 6 && Object.entries(data).every(([label, value]) => label.length > 0 && label.length <= 16 && Number.isInteger(value) && value >= 0 && value <= 100);

export const primitiveValidators = {
  base10_blocks_3digit: (s) => Number.isInteger(s.number) && s.number >= 100 && s.number <= 999,
  fraction_shape: (s) => fractionValid(s.filled, s.parts),
  unit_fraction: (s) => denominatorValid(s.parts),
  fraction_bar: (s) => fractionValid(s.numerator, s.denominator),
  unit_fraction_compare: (s) => denominatorValid(s.a) && denominatorValid(s.b),
  polygon: (s) => Number.isInteger(s.sides) && s.sides >= 3 && s.sides <= 12 && (s.rotated === undefined || typeof s.rotated === 'boolean'),
  angle: (s) => Number.isFinite(s.degrees) && s.degrees > 0 && s.degrees <= 180,
  bar_chart: (s) => Boolean(dataValid(s.data)),
  table_to_bar: (s) => Boolean(dataValid(s.data)),
  dot_plot: (s) => Array.isArray(s.values) && s.values.length >= 1 && s.values.length <= 40 && s.values.every(Number.isInteger) && Math.max(...s.values) - Math.min(...s.values) <= 12,
};

function ThreeDigitBlocks({ number }) {
  const hundreds = Math.floor(number / 100);
  const tens = Math.floor(number / 10) % 10;
  const ones = number % 10;
  return <>
    <text x="110" y="28" fontSize="24" fill="#1d4ed8">Yüzlük</text>
    <text x="300" y="28" fontSize="24" fill="#1d4ed8">Onluk</text>
    <text x="490" y="28" fontSize="24" fill="#c2410c">Birlik</text>
    {Array.from({ length: hundreds }, (_, index) => {
      const x = 32 + index % 3 * 54;
      const y = 48 + Math.floor(index / 3) * 54;
      return <g key={`hundred-${index}`} data-testid="hundred-block">
        <rect x={x} y={y} width="48" height="48" fill="#93c5fd" stroke={BLUE} strokeWidth="1.5" />
        {Array.from({ length: 9 }, (_, cell) => <g key={cell}>
          <line x1={x + (cell + 1) * 4.8} x2={x + (cell + 1) * 4.8} y1={y} y2={y + 48} stroke="#64748b" strokeWidth="0.6" />
          <line x1={x} x2={x + 48} y1={y + (cell + 1) * 4.8} y2={y + (cell + 1) * 4.8} stroke="#64748b" strokeWidth="0.6" />
        </g>)}
      </g>;
    })}
    {Array.from({ length: tens }, (_, index) => {
      const x = 240 + index % 5 * 26;
      const y = 48 + Math.floor(index / 5) * 90;
      return <g key={`ten-${index}`} data-testid="ten-block">
        <rect x={x} y={y} width="16" height="80" rx="2" fill="#93c5fd" stroke={BLUE} strokeWidth="1.5" />
        {Array.from({ length: 9 }, (_, cell) => <line key={cell} x1={x} x2={x + 16} y1={y + (cell + 1) * 8} y2={y + (cell + 1) * 8} stroke="#64748b" />)}
      </g>;
    })}
    {Array.from({ length: ones }, (_, index) => <rect key={`one-${index}`} data-testid="one-block" x={440 + index % 3 * 36} y={64 + Math.floor(index / 3) * 36} width="28" height="28" rx="3" fill="#fdba74" stroke="#ea580c" />)}
  </>;
}

// Each model uses equal-sized parts; both comparison models share the same whole.
function FractionGrid({ parts, filled, y = 80, height = 120 }) {
  const columns = parts > 20 && parts % 10 === 0 ? 10 : parts;
  const rows = parts / columns;
  const cellWidth = 480 / columns;
  const cellHeight = height / rows;
  return <g data-testid="fraction-model">
    {Array.from({ length: parts }, (_, index) => <rect key={index} data-filled={index < filled ? 'true' : 'false'} x={60 + (index % columns) * cellWidth} y={y + Math.floor(index / columns) * cellHeight} width={cellWidth} height={cellHeight} fill={index < filled ? '#93c5fd' : '#ffffff'} stroke={INK} strokeWidth="1.5" />)}
  </g>;
}

function BarChart({ data }) {
  const entries = Object.entries(data);
  const maximum = Math.max(1, ...entries.map(([, value]) => value));
  const step = Math.max(1, Math.ceil(maximum / 5));
  const top = Math.ceil(maximum / step) * step;
  const spacing = 480 / entries.length;
  return <>
    {Array.from({ length: top / step + 1 }, (_, index) => {
      const value = index * step;
      const y = 220 - value / top * 180;
      return <g key={value}><line x1="70" x2="550" y1={y} y2={y} stroke="#cbd5e1" /><text x="48" y={y + 7} fontSize="22">{value}</text></g>;
    })}
    <line x1="70" x2="70" y1="30" y2="220" stroke={INK} strokeWidth="2" />
    {entries.map(([label, value], index) => <g key={label}>
      <rect data-testid="chart-bar" x={70 + index * spacing + spacing * 0.2} y={220 - value / top * 180} width={spacing * 0.6} height={value / top * 180} fill={BLUE} />
      <text x={70 + (index + 0.5) * spacing} y="252" fontSize={label.length > 8 ? 18 : 23}>{label}</text>
    </g>)}
  </>;
}

function DataTable({ data }) {
  const entries = Object.entries(data);
  const rowHeight = 240 / (entries.length + 1);
  return <g data-testid="source-data-table">
    {[['Kategori', 'Sıklık'], ...entries].map(([label, value], index) => <g key={index}>
      <rect x="90" y={20 + index * rowHeight} width="420" height={rowHeight} fill={index === 0 ? '#dbeafe' : '#ffffff'} stroke="#94a3b8" />
      <line x1="350" x2="350" y1={20 + index * rowHeight} y2={20 + (index + 1) * rowHeight} stroke="#94a3b8" />
      <text x="220" y={20 + (index + 0.5) * rowHeight} dominantBaseline="central" fontSize="24">{label}</text>
      <text x="430" y={20 + (index + 0.5) * rowHeight} dominantBaseline="central" fontSize="24">{value}</text>
    </g>)}
  </g>;
}

function DotPlot({ values }) {
  const low = Math.min(...values);
  const high = Math.max(...values);
  const span = Math.max(1, high - low);
  const position = (value) => low === high ? 300 : 80 + (value - low) / span * 440;
  const frequencies = values.reduce((counts, value) => ({ ...counts, [value]: (counts[value] || 0) + 1 }), {});
  const maxCount = Math.max(...Object.values(frequencies));
  const separation = Math.min(28, 170 / maxCount);
  const radius = Math.min(9, separation * 0.35);
  return <>
    <line x1="60" x2="540" y1="218" y2="218" stroke={INK} strokeWidth="3" />
    {Array.from({ length: high - low + 1 }, (_, index) => {
      const value = low + index;
      const x = position(value);
      return <g key={value}>
        <line x1={x} x2={x} y1="212" y2="224" stroke={INK} strokeWidth="2" />
        <text x={x} y="252" fontSize="25">{value}</text>
        {Array.from({ length: frequencies[value] || 0 }, (_, dot) => <circle data-testid="data-dot" key={dot} cx={x} cy={200 - dot * separation} r={radius} fill={BLUE} />)}
      </g>;
    })}
  </>;
}

export const primitiveRenderers = {
  base10_blocks_3digit: ThreeDigitBlocks,
  fraction_shape: ({ parts, filled }) => <FractionGrid parts={parts} filled={filled} />,
  unit_fraction: ({ parts }) => <FractionGrid parts={parts} filled={1} />,
  fraction_bar: ({ numerator, denominator }) => <FractionGrid parts={denominator} filled={numerator} />,
  unit_fraction_compare: ({ a, b }) => <>
    <text x="300" y="35" fontSize="25">1/{a}</text><FractionGrid parts={a} filled={1} y={50} height={65} />
    <text x="300" y="155" fontSize="25">1/{b}</text><FractionGrid parts={b} filled={1} y={170} height={65} />
  </>,
  polygon: ({ sides, rotated }) => {
    const points = Array.from({ length: sides }, (_, index) => {
      const radians = -Math.PI / 2 + (rotated ? Math.PI / 7 : 0) + index * 2 * Math.PI / sides;
      return `${300 + 110 * Math.cos(radians)},${140 + 110 * Math.sin(radians)}`;
    }).join(' ');
    return <polygon points={points} fill="#dbeafe" stroke={BLUE} strokeWidth="4" strokeLinejoin="round" />;
  },
  angle: ({ degrees }) => {
    const radians = degrees * Math.PI / 180;
    const x = 300 + 145 * Math.cos(radians);
    const y = 210 - 145 * Math.sin(radians);
    return <>
      <line x1="300" y1="210" x2="445" y2="210" stroke={BLUE} strokeWidth="5" strokeLinecap="round" />
      <line data-testid="angle-ray" x1="300" y1="210" x2={x} y2={y} stroke={BLUE} strokeWidth="5" strokeLinecap="round" />
      <path d={`M345 210 A45 45 0 0 0 ${300 + 45 * Math.cos(radians)} ${210 - 45 * Math.sin(radians)}`} fill="none" stroke={INK} strokeWidth="2" />
      <circle cx="300" cy="210" r="5" fill={INK} />
    </>;
  },
  bar_chart: BarChart,
  table_to_bar: DataTable,
  dot_plot: DotPlot,
};
